'use client';

import { useEffect, useRef, useState } from 'react';
import { API_URL, apiClient } from '@/lib/api';
import { enableWebPush } from '@/utils/pushUtils';
import { clearAuth, getAccessToken, getStoredUserRaw, getValidAccessToken, isJwtExpired } from '@/lib/authStorage';
import { publishStemUnreadCount } from '@/lib/stemUnreadStore';
import { useToast } from '@/components/ui/Toast';

const POLL_MS = 8000;
const ONLINE_PING_MS = 25000;
const MAX_NOTIFICATIONS_PER_TICK = 3;
const LS_NOTIF_CONTENT_KEY = 'lisad_show_notification_content';
const NOTIF_PREVIEW_MAX = 160;

function canUseNotifications(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function isNotificationContentEnabled(): boolean {
  // Комментарий на русском: по умолчанию показываем превью, а пользователь может отключить (0) для приватности.
  try {
    return localStorage.getItem(LS_NOTIF_CONTENT_KEY) !== '0';
  } catch {
    return true;
  }
}

function formatUserTitle(u: any): string {
  // Комментарий на русском: title = "Имя (@ник)" или просто "Имя"/"@ник".
  const dn = String(u?.display_name || '').trim();
  const un = String(u?.username || '').trim();
  if (dn && un && dn !== un) return `${dn} (@${un})`;
  if (dn) return dn;
  if (un) return `@${un}`;
  return 'Новое сообщение';
}

function normalizeNotificationBody(raw: string): string {
  // Комментарий на русском: не показываем прямые URL на файлы — заменяем на человекочитаемое превью.
  const t = String(raw || '').trim();
  if (!t) return '';
  const low = t.toLowerCase();
  if ((low.startsWith('http://') || low.startsWith('https://') || low.startsWith('/')) && /\.(png|jpe?g|gif|webp|avif)(\?|$)/i.test(low)) {
    return 'Фото';
  }
  return t.length > NOTIF_PREVIEW_MAX ? `${t.slice(0, NOTIF_PREVIEW_MAX - 1)}…` : t;
}

// Комментарий на русском: звук в браузерах (особенно iOS) нужно "разблокировать" первым жестом пользователя.
let beepCtx: AudioContext | null = null;
let beepUnlocked = false;

// Комментарий на русском: создаём Audio-элемент для fallback воспроизведения (более совместимо на iOS/Safari).
let notificationAudio: HTMLAudioElement | null = null;

function getOrCreateAudioContext() {
  try {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
    if (!Ctx) return null;
    if (!beepCtx) beepCtx = new Ctx();
    return beepCtx;
  } catch {
    return null;
  }
}

function unlockBeep() {
  try {
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    ctx
      .resume()
      .then(() => {
        beepUnlocked = true;
        // Тихий "тик", чтобы Safari точно активировал аудио.
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.01);
      })
      .catch(() => {});
    
    // Комментарий на русском: также инициализируем HTMLAudioElement для fallback.
    if (!notificationAudio) {
      notificationAudio = new Audio();
      notificationAudio.volume = 0.0001;
      notificationAudio.play().catch(() => {});
    }
  } catch {
    // ignore
  }
}

function playBeep() {
  try {
    // Комментарий на русском: создаём контекст, если ещё не создан (пользователь мог зайти напрямую).
    const ctx = getOrCreateAudioContext();
    if (!ctx) return;
    
    // Комментарий на русском: если контекст suspended — пытаемся возобновить.
    if (ctx.state === 'suspended') {
      void ctx.resume().then(() => {
        beepUnlocked = true;
        actuallyPlayBeep(ctx);
      }).catch(() => {});
      return;
    }
    
    actuallyPlayBeep(ctx);
  } catch {
    // ignore
  }
}

function actuallyPlayBeep(ctx: AudioContext) {
  try {
    // Комментарий на русском: мелодичный двухтональный звук уведомления.
    const now = ctx.currentTime;
    
    // Первая нота (высокая)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.type = 'sine';
    osc1.frequency.value = 880; // A5
    gain1.gain.setValueAtTime(0.25, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    osc1.start(now);
    osc1.stop(now + 0.15);
    
    // Вторая нота (выше, с небольшой задержкой)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.value = 1318.5; // E6
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.setValueAtTime(0.2, now + 0.08);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.25);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.25);
  } catch {
    // ignore
  }
}

export default function ChatNotifications() {
  // Комментарий на русском: убираем SSR/гидрацию для плавающей кнопки (localStorage/window есть только на клиенте).
  const [mounted, setMounted] = useState(false);
  const [hasValidAccessToken, setHasValidAccessToken] = useState(false);
  const [notificationSessionKey, setNotificationSessionKey] = useState('');
  const { push: pushToast, dismiss: dismissToast } = useToast();
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  const [roomUnreadCount, setRoomUnreadCount] = useState(0);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [toasts, setToasts] = useState<Array<{ id: string; title: string; icon?: string; url: string; body?: string }>>([]);
  const toastsRef = useRef<Array<{ id: string; title: string; icon?: string; url: string; body?: string }>>([]);
  const bootstrappedRef = useRef(false);
  const meIdRef = useRef<number>(0);
  const isSuperRef = useRef(false);
  const prevByUserRef = useRef<Record<number, { lastId: number; unread: number }>>({});
  const roomsBootRef = useRef(false);
  const prevRoomsRef = useRef<Record<number, { lastId: number; unread: number }>>({});
  const supportBootRef = useRef(false);
  const prevSupportRef = useRef<{ lastId: number; unread: number }>({ lastId: 0, unread: 0 });
  const prevSupportInboxRef = useRef<Record<number, { lastId: number; unread: number }>>({});
  const stopPollingRef = useRef(false);
  const unreadCount = dmUnreadCount + roomUnreadCount + supportUnreadCount;

  useEffect(() => {
    const syncNotificationSession = () => {
      const token = getAccessToken();
      setHasValidAccessToken(!!getValidAccessToken());
      if (!token) {
        setNotificationSessionKey('');
        return;
      }
      try {
        const userId = Number(JSON.parse(getStoredUserRaw() || '{}')?.id || 0);
        setNotificationSessionKey(userId > 0 ? `user:${userId}` : `token:${token}`);
      } catch {
        setNotificationSessionKey(`token:${token}`);
      }
    };
    syncNotificationSession();
    window.addEventListener('lisad_auth_changed', syncNotificationSession);
    window.addEventListener('storage', syncNotificationSession);
    return () => {
      window.removeEventListener('lisad_auth_changed', syncNotificationSession);
      window.removeEventListener('storage', syncNotificationSession);
    };
  }, []);

  useEffect(() => {
    publishStemUnreadCount(unreadCount);
  }, [unreadCount]);

  useEffect(() => {
    let active = true;
    const requestControllers = new Set<AbortController>();
    const toastTimers = new Set<number>();
    const headerToastIds = new Set<string>();
    setMounted(true);
    stopPollingRef.current = false;
    bootstrappedRef.current = false;
    meIdRef.current = 0;
    isSuperRef.current = false;
    prevByUserRef.current = {};
    roomsBootRef.current = false;
    prevRoomsRef.current = {};
    supportBootRef.current = false;
    prevSupportRef.current = { lastId: 0, unread: 0 };
    prevSupportInboxRef.current = {};
    toastsRef.current = [];
    setToasts([]);
    setDmUnreadCount(0);
    setRoomUnreadCount(0);
    setSupportUnreadCount(0);
    publishStemUnreadCount(0);
    // Комментарий на русском: разблокируем звук на первый жест (иначе iOS/Chrome режут playback).
    const onUnlock = () => unlockBeep();
    window.addEventListener('pointerdown', onUnlock, { once: true, passive: true });
    window.addEventListener('touchstart', onUnlock, { once: true, passive: true });
    window.addEventListener('keydown', onUnlock, { once: true });
    const onBeep = () => playBeep();
    window.addEventListener('lisad-chat-beep', onBeep as any);
    
    // Комментарий на русском: на странице /messages сразу пытаемся инициализировать аудио-контекст.
    const isOnMessengerInit = window.location.pathname.startsWith('/messages');
    if (isOnMessengerInit) {
      // Если пользователь уже был на сайте, аудио мог быть разблокирован ранее.
      getOrCreateAudioContext();
      // Пробуем возобновить контекст (может быть suspended).
      if (beepCtx && beepCtx.state === 'suspended') {
        void beepCtx.resume().catch(() => {});
      }
    }
    
    // Комментарий на русском: при каждом взаимодействии пробуждаем аудио (чтобы не терялось после idle).
    const keepAudioAlive = () => {
      if (beepCtx && beepCtx.state === 'suspended') {
        void beepCtx.resume().catch(() => {});
      }
    };
    window.addEventListener('click', keepAudioAlive, { passive: true });
    window.addEventListener('touchend', keepAudioAlive, { passive: true });

    // Комментарий на русском: если открыто несколько вкладок — уведомления должен отправлять только один "лидер".
    const TAB_ID = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const LEADER_KEY = 'lisad_chat_notifications_leader';
    const LEADER_TTL_MS = 20000;
    const isLeader = () => {
      try {
        const now = Date.now();
        const raw = localStorage.getItem(LEADER_KEY);
        const cur = raw ? (JSON.parse(raw) as any) : null;
        const curId = String(cur?.id || '');
        const curTs = Number(cur?.ts || 0);
        const stale = !curId || !curTs || now - curTs > LEADER_TTL_MS;
        if (stale || curId === TAB_ID) {
          localStorage.setItem(LEADER_KEY, JSON.stringify({ id: TAB_ID, ts: now }));
          return true;
        }
        return false;
      } catch {
        // Если localStorage недоступен — лучше хоть какие-то уведомления, чем ноль.
        return true;
      }
    };

    const syncUserMeta = () => {
      try {
        const rawUser = getStoredUserRaw();
        if (!rawUser) return;
        const u = JSON.parse(rawUser);
        meIdRef.current = Number(u?.id || 0);
        isSuperRef.current = !!u?.is_superuser;
      } catch {
        // ignore
      }
    };

    const authFetch = async (url: string, init: RequestInit = {}) => {
      if (!active || stopPollingRef.current) return null;
      const doFetch = async () => {
        if (!active) return null;
        let token = getAccessToken();
        if (!token) return null;
        if (isJwtExpired(token)) {
          const refreshed = await apiClient.refreshToken();
          if (!active) return null;
          if (!refreshed) {
            clearAuth();
            stopPollingRef.current = true;
            return null;
          }
          token = getAccessToken();
          if (!token) return null;
        }
        setHasValidAccessToken(!!getValidAccessToken());
        // Комментарий на русском: ставим таймаут на сетевые запросы, чтобы не висеть минутами при проблемах сервера/сети.
        const controller = new AbortController();
        requestControllers.add(controller);
        const timeoutMs =
          url.includes('/api/chat/status/online/') ? 2500 : url.includes('/api/chat/conversations/') ? 3500 : 4500;
        const t = window.setTimeout(() => controller.abort(), timeoutMs);
        try {
          const response = await fetch(url, {
            ...init,
            signal: controller.signal,
            headers: {
              ...(init.headers as any),
              Authorization: `Bearer ${token}`,
            },
          });
          return active ? response : null;
        } catch (e: any) {
          // abort/network — считаем как "нет ответа"
          return null;
        } finally {
          requestControllers.delete(controller);
          window.clearTimeout(t);
        }
      };

      let res = await doFetch();
      if (!active || !res) return null;
      if (res.status === 401) {
        const refreshed = await apiClient.refreshToken();
        if (!active) return null;
        if (!refreshed) {
          // Комментарий на русском: чтобы не спамить 401 в консоль — сбрасываем токены и останавливаем поллинг.
          clearAuth();
          stopPollingRef.current = true;
          return null;
        }
        res = await doFetch();
      }
      return active ? res : null;
    };

    const addToast = (title: string, icon: string | undefined, url: string, body?: string) => {
      try {
        if (!active) return;
        // Комментарий на русском: тосты показываем только в активной вкладке.
        if (document.hidden) return;

        // Комментарий на русском: вне /messages — показываем уведомление в шапке сайта (единая система).
        const isOnMessengerNow = window.location.pathname.startsWith('/messages');
        if (!isOnMessengerNow) {
          playBeep();
          const toastId = pushToast({
            type: 'info',
            title,
            message: (body || '').trim() || 'Новое сообщение',
            // Комментарий на русском: уведомления из мессенджера держим дольше (10 секунд), чтобы успевали прочитать.
            durationMs: 10000,
            href: url,
            iconUrl: icon,
          });
          headerToastIds.add(toastId);
          const timer = window.setTimeout(() => {
            headerToastIds.delete(toastId);
            toastTimers.delete(timer);
          }, 10000);
          toastTimers.add(timer);
          return;
        }

        const t = { id: `${Date.now()}_${Math.random().toString(16).slice(2)}`, title, icon, url, body };
        toastsRef.current = [t, ...(toastsRef.current || [])].slice(0, 3);
        setToasts([...toastsRef.current]);

        // Звук для in-app уведомления
        playBeep();

        const timer = window.setTimeout(() => {
          toastTimers.delete(timer);
          if (!active) return;
          toastsRef.current = (toastsRef.current || []).filter((x) => x.id !== t.id);
          setToasts([...toastsRef.current]);
        }, 10000);
        toastTimers.add(timer);
      } catch {
        // ignore
      }
    };

    const loadConversations = async () => {
      try {
        if (!active) return;
        syncUserMeta();
        const response = await authFetch(`${API_URL}/api/chat/conversations/`);
        if (!active || !response || !response.ok) return;

        const conversations = await response.json();
        if (!active || !Array.isArray(conversations)) return;

        const totalUnread = conversations.reduce((sum: number, conv: any) => sum + (conv?.unread_count || 0), 0);
        setDmUnreadCount(totalUnread);

        // Комментарий на русском: уведомляем, если вкладка/экран в фоне (document.hidden) или мы не на /messages.
        const isOnMessenger = window.location.pathname.startsWith('/messages');
        let activeDmUserId = 0;
        try {
          const qs = isOnMessenger ? new URLSearchParams(window.location.search || '') : null;
          activeDmUserId = qs ? Number(qs.get('user') || 0) : 0;
          if (!activeDmUserId) activeDmUserId = Number(localStorage.getItem('lisad_active_dm_user_id') || 0);
        } catch {
          // ignore
        }
        const isHidden = document.hidden;
        const showContent = isNotificationContentEnabled();
        const leader = isLeader();
        if (!bootstrappedRef.current) {
          bootstrappedRef.current = true;
          const next: Record<number, { lastId: number; unread: number }> = {};
          for (const conv of conversations) {
            const uid = Number(conv?.user?.id || 0);
            const lastId = Number(conv?.last_message?.id || 0);
            const unread = Number(conv?.unread_count || 0);
            if (uid > 0) next[uid] = { lastId, unread };
          }
          prevByUserRef.current = next;
          // Одновременно инициализируем поддержку и комнаты (без уведомлений)
          void checkSupport(isOnMessenger, leader, !isOnMessenger || isHidden);
          void checkRooms(isOnMessenger, leader, !isOnMessenger || isHidden);
          return;
        }

        // Комментарий на русском: системные уведомления (Notification API) — только вне /messages или когда вкладка скрыта.
        const allowSystem = !isOnMessenger || isHidden;

        const prev = prevByUserRef.current || {};
        const next: Record<number, { lastId: number; unread: number }> = {};
        let remaining = MAX_NOTIFICATIONS_PER_TICK;

        for (const conv of conversations) {
          const uid = Number(conv?.user?.id || 0);
          const lastId = Number(conv?.last_message?.id || 0);
          const unread = Number(conv?.unread_count || 0);
          if (uid <= 0) continue;
          next[uid] = { lastId, unread };

          if (remaining <= 0) continue;

          const p = prev[uid] || { lastId: 0, unread: 0 };
          const fromId = Number(conv?.last_message?.from_user?.id || 0);
          const isIncomingLast = fromId === uid; // входящее сообщение от собеседника
          if (!(unread > 0 && lastId > p.lastId && isIncomingLast)) continue;

          const title = formatUserTitle(conv?.user);
          // Комментарий на русском: если у пользователя нет фото — ставим дефолтный силуэт (не favicon).
          const icon = conv?.user?.avatar_url ? String(conv.user.avatar_url) : '/uploads/chat_room_avatars/lisad_default_avatar.gif';
          const url = `/messages?user=${encodeURIComponent(String(uid))}`;

          const body = showContent ? normalizeNotificationBody(String(conv?.last_message?.message || '')) : '';

          // Комментарий на русском: если пользователь уже в этом чате — не показываем уведомление из этого же чата.
          if (!(isOnMessenger && !isHidden && activeDmUserId > 0 && activeDmUserId === uid)) {
            addToast(title, icon, url, body || undefined);
          }

          // Системное уведомление (без содержания) — только одна вкладка и только вне /messages (или в фоне).
          if (leader && allowSystem && canUseNotifications() && Notification.permission === 'granted') {
            const n = new Notification(title, { body: body || '', icon, tag: `dm:${uid}` });
            n.onclick = () => {
              try {
                window.focus();
              } catch {
                // ignore
              }
              window.location.href = url;
            };
          }

          remaining -= 1;
        }

        prevByUserRef.current = next;

        // Комнаты (уведомления + звук)
        void checkRooms(isOnMessenger, leader, allowSystem);

        // Поддержка (уведомления + звук)
        void checkSupport(isOnMessenger, leader, allowSystem);
      } catch {
        // ignore
    }
  };

    async function checkRooms(isOnMessenger: boolean, leader: boolean, allowSystem: boolean) {
      try {
        if (!active) return;
        syncUserMeta();
        const res = await authFetch(`${API_URL}/api/chat/rooms/`);
        if (!active) return;
        if (!res || !res.ok) {
          // Если комнаты недоступны (старая БД/ошибка) — не ломаем всю систему уведомлений.
          setRoomUnreadCount(0);
          if (!roomsBootRef.current) roomsBootRef.current = true;
          return;
        }
        const list = await res.json();
        if (!active || !Array.isArray(list)) return;

        const totalUnread = list.reduce((sum: number, it: any) => sum + (Number(it?.unread_count || 0) || 0), 0);
        setRoomUnreadCount(totalUnread);

        const isHidden = document.hidden;
        const showContent = isNotificationContentEnabled();

        // Комментарий на русском: если пользователь уже находится в этой же комнате — не показываем уведомление.
        let activeRoomId = 0;
        try {
          const qs = isOnMessenger ? new URLSearchParams(window.location.search || '') : null;
          activeRoomId = qs ? Number(qs.get('room') || 0) : 0;
          if (!activeRoomId) activeRoomId = Number(localStorage.getItem('lisad_active_room_id') || 0);
        } catch {
          // ignore
        }

        const prev = prevRoomsRef.current || {};
        const next: Record<number, { lastId: number; unread: number }> = {};
        let remaining = MAX_NOTIFICATIONS_PER_TICK;

        for (const item of list) {
          const room = item?.room || {};
          const roomId = Number(room?.id || 0) || 0;
          if (roomId <= 0) continue;

          const last = item?.last_message || null;
          const lastId = Number(last?.id || 0) || 0;
          const unread = Number(item?.unread_count || 0) || 0;
          next[roomId] = { lastId, unread };

          if (!roomsBootRef.current) continue;
          if (remaining <= 0) continue;

          const p = prev[roomId] || { lastId: 0, unread: 0 };
          const fromId = Number(last?.from_user?.id || 0) || 0;
          const isIncomingLast = fromId > 0 && fromId !== (meIdRef.current || 0);
          if (!(unread > 0 && lastId > p.lastId && isIncomingLast)) continue;

          const title = String(room?.title || '').trim() || `Комната #${roomId}`;
          const icon = room?.avatar_url ? String(room.avatar_url) : '/pwa-icon.svg';
          const url = `/messages?room=${encodeURIComponent(String(roomId))}`;

          const sender = formatUserTitle(last?.from_user);
          const rawText = String(last?.message || '').trim();
          const bodyText = rawText ? `${sender}: ${normalizeNotificationBody(rawText)}` : sender;
          const body = showContent ? bodyText : '';

          if (!(isOnMessenger && !isHidden && activeRoomId > 0 && activeRoomId === roomId)) {
            addToast(title, icon, url, body || undefined);
          }

          if (leader && allowSystem && canUseNotifications() && Notification.permission === 'granted') {
            const n = new Notification(title, { body: body || '', icon, tag: `room:${roomId}` });
            n.onclick = () => {
              try {
                window.focus();
              } catch {}
              window.location.href = url;
            };
          }

          remaining -= 1;
        }

        prevRoomsRef.current = next;
        roomsBootRef.current = true;
      } catch {
        // ignore
      }
    }

    async function checkSupport(isOnMessenger: boolean, leader: boolean, allowSystem: boolean) {
      try {
        if (!active) return;
        syncUserMeta();
        let supportOpen = false;
        let activeSupportUserId = 0;
        try {
          const qs = isOnMessenger ? new URLSearchParams(window.location.search || '') : null;
          supportOpen = !!qs && (qs.get('support') === '1' || qs.get('support') === 'true');
          activeSupportUserId = supportOpen ? Number(qs?.get('support_user') || 0) : 0;
          if (isOnMessenger && !supportOpen) supportOpen = localStorage.getItem('lisad_active_support_open') === '1';
          if (isOnMessenger && !activeSupportUserId) activeSupportUserId = Number(localStorage.getItem('lisad_active_support_user_id') || 0);
        } catch {
          // ignore
        }
        const isHidden = document.hidden;
        const showContent = isNotificationContentEnabled();

        if (isSuperRef.current) {
          const res = await authFetch(`${API_URL}/api/chat/support/inbox/?limit=50`);
          if (!active || !res || !res.ok) return;
          const inbox = await res.json();
          if (!active || !Array.isArray(inbox)) return;

          const totalSupportUnread = inbox.reduce((sum: number, conv: any) => sum + (conv?.unread_count || 0), 0);
          setSupportUnreadCount(totalSupportUnread);

          const prev = prevSupportInboxRef.current || {};
          const next: Record<number, { lastId: number; unread: number }> = {};

          let notified = false;
          for (const conv of inbox) {
            const uid = Number(conv?.user?.id || 0);
            const lastId = Number(conv?.last_message?.id || 0);
            const unread = Number(conv?.unread_count || 0);
            const fromId = Number(conv?.last_message?.from_user?.id || 0);
            if (uid <= 0) continue;
            next[uid] = { lastId, unread };

            if (!supportBootRef.current) continue;
            if (notified) continue;

            const p = prev[uid] || { lastId: 0, unread: 0 };
            const isIncoming = fromId === uid;
            if (unread > 0 && lastId > p.lastId && isIncoming) {
              const title = formatUserTitle(conv?.user);
              // Комментарий на русском: если у пользователя нет фото — ставим дефолтный силуэт (не favicon).
              const icon = conv?.user?.avatar_url ? String(conv.user.avatar_url) : '/uploads/chat_room_avatars/lisad_default_avatar.gif';
              const url = `/messages?support=1&support_user=${encodeURIComponent(String(uid))}`;
              const body = showContent ? normalizeNotificationBody(String(conv?.last_message?.message || '')) : '';

              // Комментарий на русском: если открыт этот же поток поддержки — не дублируем уведомлением.
              if (!(isOnMessenger && !isHidden && supportOpen && activeSupportUserId > 0 && activeSupportUserId === uid)) {
                addToast(title, icon, url, body || undefined);
              }

              // Системное уведомление (без содержания) — только одна вкладка и только вне /messages (или в фоне).
              if (leader && allowSystem && canUseNotifications() && Notification.permission === 'granted') {
                const n = new Notification(title, { body: body || '', icon, tag: `support:${uid}` });
                n.onclick = () => {
                  try {
                    window.focus();
                  } catch {}
                  window.location.href = url;
                };
              }
              notified = true;
            }
          }

          prevSupportInboxRef.current = next;
          supportBootRef.current = true;
          return;
        }

        // Обычный пользователь: одно уведомление по поддержке
        const res = await authFetch(`${API_URL}/api/chat/support/unread/`);
        if (!active || !res || !res.ok) return;
        const data = await res.json();
        if (!active) return;
        const unread = Number(data?.unread || 0);
        const lastId = Number(data?.last_id || 0);
        setSupportUnreadCount(unread);

        if (!supportBootRef.current) {
          prevSupportRef.current = { lastId, unread };
          supportBootRef.current = true;
          return;
          }

        const prev = prevSupportRef.current;
        prevSupportRef.current = { lastId, unread };

        if (unread > 0 && lastId > (prev?.lastId || 0)) {
          const url = `/messages?support=1`;
          if (!(isOnMessenger && !isHidden && supportOpen)) {
            addToast('Поддержка', '/favicon.ico', url);
          }
          if (leader && allowSystem && canUseNotifications() && Notification.permission === 'granted') {
            const n = new Notification('Поддержка', { body: '', tag: 'support' });
            n.onclick = () => {
              try {
                window.focus();
              } catch {}
              window.location.href = url;
            };
          }
        }
      } catch {
        // ignore
      }
    }

    const pingOnline = async () => {
      try {
        syncUserMeta();
        await authFetch(`${API_URL}/api/chat/status/online/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_online: true }),
        });
      } catch {
        // ignore
      }
    };

    syncUserMeta();

    let tUnread: any = null;
    let tOnline: any = null;
    const schedule = () => {
      if (tUnread) clearInterval(tUnread);
      if (tOnline) clearInterval(tOnline);
      // Комментарий на русском: в фоне реже поллим, чтобы не жечь батарею/CPU.
      const unreadMs = document.hidden ? 20000 : POLL_MS;
      const onlineMs = document.hidden ? 60000 : ONLINE_PING_MS;
      tUnread = setInterval(loadConversations, unreadMs);
      tOnline = setInterval(pingOnline, onlineMs);
    };
    const onVis = () => schedule();

    // Комментарий на русском: polling живёт вместе с актуальной auth-сессией и перезапускается после входа/выхода.
    if (notificationSessionKey) {
      void loadConversations();
      void pingOnline();
      document.addEventListener('visibilitychange', onVis);
      schedule();
    }
    return () => {
      active = false;
      requestControllers.forEach((controller) => controller.abort());
      toastTimers.forEach((timer) => window.clearTimeout(timer));
      headerToastIds.forEach((toastId) => dismissToast(toastId));
      headerToastIds.clear();
      try {
        const lease = JSON.parse(localStorage.getItem(LEADER_KEY) || 'null');
        if (String(lease?.id || '') === TAB_ID) localStorage.removeItem(LEADER_KEY);
      } catch {
        // localStorage может быть недоступен в приватном режиме.
      }
      if (tUnread) clearInterval(tUnread);
      if (tOnline) clearInterval(tOnline);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pointerdown', onUnlock as any);
      window.removeEventListener('touchstart', onUnlock as any);
      window.removeEventListener('keydown', onUnlock as any);
      window.removeEventListener('lisad-chat-beep', onBeep as any);
      window.removeEventListener('click', keepAudioAlive as any);
      window.removeEventListener('touchend', keepAudioAlive as any);
    };
  }, [dismissToast, notificationSessionKey, pushToast]);

  if (!mounted) return null;
  // Не показываем кнопку, если пользователь не авторизован.
  if (!hasValidAccessToken) return null;

  const isOnMessenger = window.location.pathname.startsWith('/messages');
  const requestNavigate = (href: string) => {
    if (typeof window === 'undefined') return false;
    if (!(window as any).__newsEditorActive) return false;
    window.dispatchEvent(new CustomEvent('news_editor:navigate', { detail: { href } }));
    return true;
  };

  return (
    <>
      {/* Комментарий на русском: в мессенджере шапки нет — поэтому оставляем старые in-app тосты прямо в UI мессенджера. */}
      {isOnMessenger && toasts.length > 0 && (
        <div
          className="fixed left-3 right-3 sm:left-auto sm:right-6 sm:w-80 z-[60] space-y-2 pointer-events-none"
          style={{ top: 'calc(var(--app-top, 0px) + env(safe-area-inset-top, 0px) + 12px)' }}
        >
          {toasts.map((t) => (
            <button
              key={t.id}
              className="pointer-events-auto w-full text-left px-3 py-2 rounded-xl border border-site-border-control/40 bg-site-bg/70 backdrop-blur-md hover:bg-site-bg/85 transition-colors"
              onClick={() => {
                toastsRef.current = (toastsRef.current || []).filter((x) => x.id !== t.id);
                setToasts([...toastsRef.current]);
                if (!requestNavigate(t.url)) {
                  window.location.href = t.url;
                }
              }}
              title="Открыть чат"
            >
              <div className="flex items-center gap-3">
                {t.icon ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={t.icon} alt="" className="w-10 h-10 rounded-full object-cover border border-site-border" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-site-surface/60 border border-site-border flex items-center justify-center text-site-text font-semibold">
                    {String(t.title || '•').trim().slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-site-text truncate">{t.title}</div>
                  {t.body ? <div className="text-xs text-site-text/80 truncate">{t.body}</div> : null}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Комментарий на русском: плавающая кнопка мессенджера (Liquid Glass, расширяется при hover с надписью STEM). */}
      {!isOnMessenger && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="relative group">
            {/* Бейдж с количеством непрочитанных */}
            {unreadCount > 0 && (
              <div className="absolute -top-2 -right-2 z-10 bg-site-danger text-site-on-accent text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse">
                {unreadCount > 99 ? '99+' : unreadCount}
              </div>
            )}

            {/* Кнопка "жидкое стекло" (Liquid Glass) с анимацией расширения и появлением STEM */}
            <button
              onClick={() => {
                unlockBeep();
                // Комментарий на русском: сначала навигация, push в фоне (чтобы ошибка push не блокировала переход).
                if (!requestNavigate('/messages')) {
                  window.location.href = '/messages';
                }
                // Пробуем включить push в фоне, не блокируя навигацию.
                void enableWebPush(API_URL).catch(() => {});
              }}
              className="
                messenger-fab-pulse
                flex items-center justify-center gap-0 overflow-hidden
                h-14 w-14 sm:h-14 sm:w-14 rounded-full
                sm:group-hover:w-44 sm:group-hover:rounded-[28px] sm:group-hover:gap-2.5
                backdrop-blur-xl border
                transition-all duration-300 ease-in-out
                active:scale-90 sm:active:scale-[0.97]
                focus:outline-none focus-visible:ring-2 focus-visible:ring-site-success/60
              "
              style={{
                // Комментарий на русском: градиент "жидкое стекло" + полупрозрачность.
                background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.65) 0%, rgba(15, 23, 42, 0.55) 50%, rgba(30, 58, 65, 0.5) 100%)',
                borderColor: 'rgba(100, 116, 139, 0.35)',
              }}
              // Комментарий на русском: тень/свечение включаем только при наведении (в обычном состоянии тени нет).
              onMouseEnter={(e) => {
                if (window.matchMedia('(min-width: 640px)').matches) {
                  e.currentTarget.style.background = 'linear-gradient(145deg, rgba(16, 185, 129, 0.55) 0%, rgba(5, 150, 105, 0.45) 50%, rgba(4, 120, 87, 0.4) 100%)';
                  e.currentTarget.style.borderColor = 'rgba(52, 211, 153, 0.5)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(16, 185, 129, 0.35), 0 0 25px rgba(52, 211, 153, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(145deg, rgba(30, 41, 59, 0.65) 0%, rgba(15, 23, 42, 0.55) 50%, rgba(30, 58, 65, 0.5) 100%)';
                e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.35)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              title={unreadCount > 0 ? `${unreadCount} новых сообщений` : 'Открыть мессенджер'}
            >
              {/* Надпись STEM (появляется при hover на десктопе) */}
              <span
                className="
                  hidden sm:block
                  max-w-0 opacity-0 overflow-hidden whitespace-nowrap
                  sm:group-hover:max-w-[100px] sm:group-hover:opacity-100 sm:group-hover:ml-2
                  transition-all duration-300 ease-out
                "
                style={{
                  fontFamily: 'GlitchGoblin, system-ui, sans-serif',
                  fontSize: '17px',
                  color: '#fff',
                  textShadow: '0 2px 10px rgba(52, 211, 153, 0.7), 0 0 20px rgba(16, 185, 129, 0.4)',
                  letterSpacing: '0.14em',
                }}
              >
                S T E M
              </span>

              {/* Иконка чата */}
              <svg
                className="
                  w-6 h-6 flex-shrink-0
                  text-site-text sm:group-hover:text-site-text
                  transition-colors duration-300
                  sm:group-hover:drop-shadow-[0_0_8px_rgba(52,211,153,0.6)]
                "
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
